import React from 'react'

/**
 * Shared detail-page layout vocabulary.
 *
 * Every detail page (Workspace, Company, Item, Scenario) shares the same
 * conceptual scaffolding:
 *
 *   - Hero        — title + subtitle + right-side actions + status pills
 *   - Answer      — "what's our read" (emphasised, single large block)
 *   - Why         — drivers / thesis / current view
 *   - Watch       — what to watch next (monitors, watchpoints)
 *   - Evidence    — source records, forecasts, supporting tables
 *
 * `DetailLayout.*` components encode this vocabulary with consistent
 * spacing, headings, and accessibility hooks (`<section aria-labelledby>`,
 * heading levels, etc.). Pages compose these instead of hand-rolling card
 * markup so a copy or density tweak lands in one place.
 *
 * Usage:
 *
 * ```tsx
 * <DetailLayout>
 *   <DetailLayout.Hero
 *     kicker="Live forecast workspace"
 *     title={displayTitle}
 *     subtitle={displaySubtitle}
 *     right={<DownloadReportButton ... />}
 *     pills={[<Pill>14 forecasts</Pill>, <Freshness ... />]}
 *   />
 *   <DetailLayout.Answer headline={answerHeadline}>{answerBody}</DetailLayout.Answer>
 *   <DetailLayout.Section title="Why we hold this view">...</DetailLayout.Section>
 *   <DetailLayout.Watch>...</DetailLayout.Watch>
 *   <DetailLayout.Evidence>...</DetailLayout.Evidence>
 * </DetailLayout>
 * ```
 */

type HeroProps = {
  kicker?: React.ReactNode
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** Right-aligned action area (buttons, download triggers, etc.). */
  right?: React.ReactNode
  /** Inline status pills (forecast count, freshness, monitor count). */
  pills?: React.ReactNode
  /** Optional descriptor below subtitle (e.g. "Original question: ..."). */
  context?: React.ReactNode
  className?: string
}

function Hero({ kicker, title, subtitle, right, pills, context, className }: HeroProps) {
  return (
    <header className={['detail-hero', className].filter(Boolean).join(' ')} style={{ marginTop: 16 }}>
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          {kicker ? (
            <div
              className="muted"
              style={{
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: 0.7,
                fontWeight: 850,
              }}
            >
              {kicker}
            </div>
          ) : null}
          <h1 style={{ margin: kicker ? '4px 0 0' : 0, lineHeight: 1.15, fontSize: 28, fontWeight: 800 }}>
            {title}
          </h1>
          {subtitle ? <p className="muted" style={{ marginTop: 8 }}>{subtitle}</p> : null}
          {context ? <div style={{ marginTop: 8 }}>{context}</div> : null}
        </div>
        {right ? <div style={{ flex: '0 0 auto' }}>{right}</div> : null}
      </div>
      {pills ? (
        <div className="row" style={{ gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {pills}
        </div>
      ) : null}
    </header>
  )
}

type SectionProps = {
  /** Section heading. Provide as a string for the default styling, or a
   *  node to fully customise. */
  title?: React.ReactNode
  /** Optional sub-header description below the title. */
  description?: React.ReactNode
  /** Accent the section visually (e.g. for the "Answer" panel). */
  emphasis?: 'answer' | 'watch' | 'evidence' | 'why' | 'none'
  /** Provide a stable id so headings + aria-labelledby chain works. */
  id?: string
  /** Append additional class names. */
  className?: string
  children: React.ReactNode
}

const EMPHASIS_STYLE: Partial<Record<NonNullable<SectionProps['emphasis']>, React.CSSProperties>> = {
  answer: { borderLeft: '3px solid var(--accent, #6366f1)' },
  watch: { borderLeft: '3px solid var(--warn, #f59e0b)' },
  evidence: { borderLeft: '3px solid var(--ok, #16a34a)' },
}

function Section({ title, description, emphasis = 'none', id, className, children }: SectionProps) {
  const headingId = id ? `${id}-heading` : undefined
  const accentStyle = emphasis !== 'none' ? EMPHASIS_STYLE[emphasis] : undefined
  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={['card', className].filter(Boolean).join(' ')}
      style={{ marginTop: 16, ...(accentStyle || {}) }}
    >
      {title ? (
        <h2 id={headingId} style={{ margin: '0 0 8px', fontSize: 18 }}>
          {title}
        </h2>
      ) : null}
      {description ? (
        <p className="muted" style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.55 }}>
          {description}
        </p>
      ) : null}
      {children}
    </section>
  )
}

function Answer(props: Omit<SectionProps, 'emphasis'> & { headline?: React.ReactNode }) {
  const { title = 'Answer', headline, children, ...rest } = props
  return (
    <Section title={title} emphasis="answer" id={rest.id || 'answer'} {...rest}>
      {headline ? <h3 style={{ margin: '0 0 8px', fontSize: 22, lineHeight: 1.2 }}>{headline}</h3> : null}
      {children}
    </Section>
  )
}

function Why(props: Omit<SectionProps, 'emphasis'>) {
  return <Section emphasis="why" id={props.id || 'why'} title={props.title || 'Why we hold this view'} {...props} />
}

function Watch(props: Omit<SectionProps, 'emphasis'>) {
  return <Section emphasis="watch" id={props.id || 'watch'} title={props.title || 'What to watch next'} {...props} />
}

function Evidence(props: Omit<SectionProps, 'emphasis'>) {
  return (
    <Section emphasis="evidence" id={props.id || 'evidence'} title={props.title || 'Evidence trail'} {...props} />
  )
}

function DetailLayout({ children }: { children: React.ReactNode }) {
  return <>{children}</>
}

DetailLayout.Hero = Hero
DetailLayout.Section = Section
DetailLayout.Answer = Answer
DetailLayout.Why = Why
DetailLayout.Watch = Watch
DetailLayout.Evidence = Evidence

export default DetailLayout
export type { HeroProps, SectionProps }
